import React, { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

const ADSENSE_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT || "";
const SLOTS = {
  "dashboard-top": process.env.REACT_APP_ADSENSE_SLOT_DASHBOARD || "",
  "community-top": process.env.REACT_APP_ADSENSE_SLOT_COMMUNITY || "",
};

/**
 * AdBanner
 * - Hidden for premium users (any Sovereign tier or founder)
 * - Renders real Google AdSense unit when REACT_APP_ADSENSE_CLIENT is set
 * - Otherwise shows a tasteful house placeholder that doubles as an Ascend CTA
 */
export default function AdBanner({ slot = "dashboard-top", format = "horizontal", className = "" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const insRef = useRef(null);

  const shouldRender = user && !user.premium;

  useEffect(() => {
    if (!shouldRender || !ADSENSE_CLIENT || !insRef.current) return;
    try {
      // AdSense expects window.adsbygoogle to be pushed once per <ins> unit
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (error) {
      // AdSense script may still be loading or blocked by an extension — non-fatal.
      if (process.env.NODE_ENV !== "production") console.debug("adsbygoogle push failed:", error);
    }
  }, [shouldRender]);

  if (!shouldRender) return null;

  const height = format === "horizontal" ? "min-h-[90px]" : "min-h-[250px]";

  if (ADSENSE_CLIENT && SLOTS[slot]) {
    return (
      <div className={`w-full ${className}`} data-testid={`ad-banner-${slot}`} data-ad-slot={slot}>
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: "block" }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={SLOTS[slot]}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      </div>
    );
  }

  // House placeholder — Ascend CTA
  return (
    <div
      data-testid={`ad-banner-${slot}`}
      data-ad-slot={slot}
      data-ad-placeholder="true"
      className={`w-full ${height} rounded-sm border border-amber-500/25 bg-stone-900/40 backdrop-blur-sm flex items-center justify-between gap-4 px-4 sm:px-6 py-3 ${className}`}
    >
      <div className="min-w-0">
        <div className="font-mono text-[0.55rem] uppercase tracking-[0.4em] text-stone-500 mb-1">Advertisement</div>
        <div className="font-serif-body text-stone-200 text-sm sm:text-base leading-snug truncate">
          Silence the heralds. <span className="gold">Ascend to Sovereign</span> and read the archive without interruption.
        </div>
      </div>
      <button
        data-testid={`ad-banner-cta-${slot}`}
        onClick={() => navigate("/app?upgrade=1")}
        className="btn-ghost text-[0.65rem] shrink-0"
      >
        Ascend
      </button>
    </div>
  );
}
